import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { 
  Shield, FileText, Settings, LogOut, BarChart3, Search, Globe,
  Plus, Edit2, Trash2, MapPin, Tag, Cpu, ChevronRight, Save, X,
  Download, Upload, FileSpreadsheet, Database, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSEO = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('brands');
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [cities, setCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [editModal, setEditModal] = useState({ open: false, type: null, data: null });
  const [formData, setFormData] = useState({});
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [brandsRes, modelsRes, citiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/seo/brands`),
        axios.get(`${API_URL}/api/seo/models`),
        axios.get(`${API_URL}/api/seo/cities`)
      ]);
      setBrands(brandsRes.data);
      setModels(modelsRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      toast.error('Error al cargar datos SEO');
    } finally {
      setLoading(false);
    }
  };

  // CSV Template Download
  const downloadTemplate = (type) => {
    let headers, exampleRows, filename;
    
    switch (type) {
      case 'brands':
        headers = ['name', 'slug', 'categories', 'description', 'meta_title', 'meta_description'];
        exampleRows = [
          ['Samsung', 'samsung', 'moviles,tablets,accesorios', 'Reparación de dispositivos Samsung', 'Reparación Samsung - Arreglatelo', 'Servicio técnico Samsung. Reparamos móviles, tablets y accesorios Samsung.'],
          ['LG', 'lg', 'moviles,electrodomesticos', 'Reparación de dispositivos LG', 'Reparación LG - Arreglatelo', 'Servicio técnico LG. Reparamos móviles y electrodomésticos.']
        ];
        filename = 'plantilla_marcas.csv';
        break;
      case 'models':
        headers = ['name', 'slug', 'brand_name', 'category', 'repair_time', 'repair_cost_range', 'common_problems', 'description', 'meta_title', 'meta_description'];
        exampleRows = [
          ['Galaxy S24', 'galaxy-s24', 'Samsung', 'moviles', '2-4 días', '89€ - 249€', 'Pantalla rota,Batería,No carga', 'Reparación Samsung Galaxy S24', 'Reparar Galaxy S24 - Arreglatelo', 'Reparamos tu Samsung Galaxy S24. Pantalla, batería y más.'],
          ['Conga 8090', 'conga-8090', 'Cecotec', 'robots', '3-5 días', '49€ - 129€', 'No aspira,Rueda atascada,Error sensor', 'Reparación Cecotec Conga 8090', 'Reparar Conga 8090 - Arreglatelo', 'Reparamos tu robot aspirador Conga 8090.']
        ];
        filename = 'plantilla_modelos.csv';
        break;
      case 'cities':
        headers = ['name', 'slug', 'province', 'postal_codes', 'local_phone', 'h1_title', 'intro_text', 'meta_title', 'meta_description'];
        exampleRows = [
          ['Zaragoza', 'zaragoza', 'Zaragoza', '50001,50002,50003', '624 47 32 93', 'Reparación de Electrónica en Zaragoza', 'Servicio técnico en Zaragoza para consolas, móviles y robots.', 'Reparación Electrónica Zaragoza - Arreglatelo', 'Reparamos consolas, móviles y robots aspiradores en Zaragoza.'],
          ['Alicante', 'alicante', 'Alicante', '03001,03002,03003', '624 47 32 93', 'Reparación de Electrónica en Alicante', 'Servicio técnico en Alicante para todo tipo de electrónica.', 'Reparación Electrónica Alicante - Arreglatelo', 'Reparamos consolas, móviles y electrodomésticos en Alicante.']
        ];
        filename = 'plantilla_ciudades.csv';
        break;
      default:
        return;
    }
    
    // Create CSV content with BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(';'), ...exampleRows.map(row => row.join(';'))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Plantilla ${filename} descargada`);
  };

  // Export current data to CSV
  const exportData = (type) => {
    let data, headers, filename;
    
    switch (type) {
      case 'brands':
        headers = ['name', 'slug', 'categories', 'description', 'meta_title', 'meta_description'];
        data = brands.map(b => [
          b.name, b.slug, (b.categories || []).join(','), b.description || '', b.meta_title || '', b.meta_description || ''
        ]);
        filename = 'marcas_export.csv';
        break;
      case 'models':
        headers = ['name', 'slug', 'brand_name', 'category', 'repair_time', 'repair_cost_range', 'common_problems', 'description', 'meta_title', 'meta_description'];
        data = models.map(m => [
          m.name, m.slug, m.brand_name || '', m.category || '', m.repair_time || '', m.repair_cost_range || '',
          (m.common_problems || []).join(','), m.description || '', m.meta_title || '', m.meta_description || ''
        ]);
        filename = 'modelos_export.csv';
        break;
      case 'cities':
        headers = ['name', 'slug', 'province', 'postal_codes', 'local_phone', 'h1_title', 'intro_text', 'meta_title', 'meta_description'];
        data = cities.map(c => [
          c.name, c.slug, c.province || '', (c.postal_codes || []).join(','), c.local_phone || '',
          c.h1_title || '', c.intro_text || '', c.meta_title || '', c.meta_description || ''
        ]);
        filename = 'ciudades_export.csv';
        break;
      default:
        return;
    }
    
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(';'), ...data.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Datos exportados a ${filename}`);
  };

  // Import CSV
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImporting(true);
    
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('El archivo está vacío o no tiene datos');
        return;
      }
      
      // Parse CSV (handle both , and ; as separators)
      const separator = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(separator).map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
      
      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(separator).map(v => v.trim().replace(/^"|"$/g, ''));
        if (values.length < 2) continue;
        
        const item = {};
        headers.forEach((header, index) => {
          let value = values[index] || '';
          // Handle array fields
          if (['categories', 'common_problems', 'postal_codes'].includes(header)) {
            item[header] = value ? value.split(',').map(v => v.trim()) : [];
          } else {
            item[header] = value;
          }
        });
        
        // Generate slug if not provided
        if (!item.slug && item.name) {
          item.slug = item.name.toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        }
        
        items.push(item);
      }
      
      if (items.length === 0) {
        toast.error('No se encontraron datos válidos en el archivo');
        return;
      }
      
      // Send to backend
      const response = await axios.post(
        `${API_URL}/api/seo/admin/import/${activeTab}`,
        { items },
        { withCredentials: true }
      );
      
      toast.success(`${response.data.imported} ${activeTab === 'brands' ? 'marcas' : activeTab === 'models' ? 'modelos' : 'ciudades'} importadas correctamente`);
      fetchData();
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const openEditModal = (type, data = null) => {
    setFormData(data || getDefaultForm(type));
    setEditModal({ open: true, type, data });
  };

  const getDefaultForm = (type) => {
    switch (type) {
      case 'brand':
        return { name: '', slug: '', description: '', meta_title: '', meta_description: '', categories: [] };
      case 'model':
        return { 
          name: '', slug: '', brand_id: '', brand_name: '', category: '', 
          description: '', common_problems: [], repair_time: '', repair_cost_range: '',
          meta_title: '', meta_description: ''
        };
      case 'city':
        return { name: '', slug: '', province: '', postal_codes: [], meta_title: '', meta_description: '', h1_title: '', intro_text: '', local_phone: '624 47 32 93' };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    try {
      const { type, data } = editModal;
      
      if (data) {
        // Update existing
        await axios.put(`${API_URL}/api/seo/admin/${type}s/${formData.slug}`, formData, { withCredentials: true });
        toast.success(`${type} actualizado`);
      } else {
        // Create new
        await axios.post(`${API_URL}/api/seo/admin/${type}s`, formData, { withCredentials: true });
        toast.success(`${type} creado`);
      }
      
      setEditModal({ open: false, type: null, data: null });
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
  };

  const handleDelete = async (type, slug) => {
    if (!window.confirm(`¿Eliminar este ${type}?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/seo/admin/${type}s/${slug}`, { withCredentials: true });
      toast.success(`${type} eliminado`);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const filteredItems = () => {
    const query = searchQuery.toLowerCase();
    switch (activeTab) {
      case 'brands':
        return brands.filter(b => b.name.toLowerCase().includes(query) || b.slug.includes(query));
      case 'models':
        return models.filter(m => m.name.toLowerCase().includes(query) || m.brand_name?.toLowerCase().includes(query));
      case 'cities':
        return cities.filter(c => c.name.toLowerCase().includes(query) || c.province?.toLowerCase().includes(query));
      default:
        return [];
    }
  };

  const loadExtendedData = async () => {
    if (!window.confirm('¿Cargar todas las ciudades de España (+350) y marcas de electrónica (+200)? Esto añadirá datos al sistema.')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/seo/admin/load-extended-data`, {}, { withCredentials: true });
      toast.success(`Datos cargados: ${response.data.cities_loaded} ciudades, ${response.data.brands_loaded} marcas`);
      fetchData();
    } catch (error) {
      toast.error('Error al cargar datos extendidos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="admin-seo">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#0A0A0A] text-white flex flex-col">
        <div className="p-6">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0047FF] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div>
              <span className="font-heading font-bold text-lg">Arreglatelo</span>
              <p className="text-xs text-[#A1A1AA]">Admin Panel</p>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-4">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <BarChart3 className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Dashboard</span>
          </Link>
          <Link to="/admin/warranties" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Shield className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Garantías</span>
          </Link>
          <Link to="/admin/invoices" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <FileText className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Facturas</span>
          </Link>
          <Link to="/admin/seo" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 mb-1">
            <Globe className="w-5 h-5" />
            <span className="font-medium">SEO & Contenido</span>
          </Link>
          <Link to="/admin/marketing" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Tag className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Marketing</span>
          </Link>
          <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors mb-1">
            <Settings className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">Configuración</span>
          </Link>
        </nav>

        <div className="p-4 border-t border-white/10">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#A1A1AA] hover:text-white hover:bg-white/5"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">SEO & Contenido</h1>
              <p className="text-[#52525B]">Gestiona marcas, modelos y páginas de ciudad para SEO</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { id: 'brands', label: 'Marcas', icon: Tag, count: brands.length },
              { id: 'models', label: 'Modelos', icon: Cpu, count: models.length },
              { id: 'cities', label: 'Ciudades', icon: MapPin, count: cities.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-[#0047FF] text-white' 
                    : 'bg-white text-[#52525B] hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search and Add */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder={`Buscar ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Import/Export Buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => downloadTemplate(activeTab)}
                title="Descargar plantilla CSV"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Plantilla
              </Button>
              <Button 
                variant="outline"
                onClick={() => exportData(activeTab)}
                title="Exportar datos actuales"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
              <Button 
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                title="Importar desde CSV"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importando...' : 'Importar'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            
            <Button 
              variant="outline"
              onClick={loadExtendedData}
              title="Cargar todas las ciudades de España y marcas de electrónica"
              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              <Database className="w-4 h-4 mr-2" />
              Cargar España Completo
            </Button>
            
            <Button 
              className="bg-[#0047FF] hover:bg-[#003BCC]"
              onClick={() => openEditModal(activeTab.slice(0, -1))}
            >
              <Plus className="w-4 h-4 mr-2" />
              Añadir {activeTab === 'brands' ? 'Marca' : activeTab === 'models' ? 'Modelo' : 'Ciudad'}
            </Button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {activeTab === 'brands' && (
                      <>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Marca</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Slug</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Categorías</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">URL</th>
                        <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Acciones</th>
                      </>
                    )}
                    {activeTab === 'models' && (
                      <>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Modelo</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Marca</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Categoría</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Precio</th>
                        <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Acciones</th>
                      </>
                    )}
                    {activeTab === 'cities' && (
                      <>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Ciudad</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">Provincia</th>
                        <th className="text-left px-6 py-3 text-sm font-medium text-gray-500">URL</th>
                        <th className="text-right px-6 py-3 text-sm font-medium text-gray-500">Acciones</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredItems().map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      {activeTab === 'brands' && (
                        <>
                          <td className="px-6 py-4 font-medium">{item.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{item.slug}</td>
                          <td className="px-6 py-4">
                            <div className="flex gap-1 flex-wrap">
                              {item.categories?.map((cat, i) => (
                                <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <a 
                              href={`https://rma.arreglatelo.es/reparacion/${item.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                            >
                              /reparacion/{item.slug}
                              <ChevronRight className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('brand', item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('brand', item.slug)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      )}
                      {activeTab === 'models' && (
                        <>
                          <td className="px-6 py-4 font-medium">{item.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{item.brand_name}</td>
                          <td className="px-6 py-4">
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                              {item.category}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">{item.repair_cost_range}</td>
                          <td className="px-6 py-4 text-right">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('model', item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('model', item.slug)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      )}
                      {activeTab === 'cities' && (
                        <>
                          <td className="px-6 py-4 font-medium">{item.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{item.province}</td>
                          <td className="px-6 py-4">
                            <a 
                              href={`https://rma.arreglatelo.es/reparacion-en/${item.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                            >
                              /reparacion-en/{item.slug}
                              <ChevronRight className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Button size="sm" variant="ghost" onClick={() => openEditModal('city', item)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete('city', item.slug)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredItems().length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No se encontraron resultados
                </div>
              )}
            </div>
          )}

          {/* Info Cards */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-medium mb-2">URLs SEO Generadas</h3>
              <p className="text-3xl font-bold text-[#0047FF]">{brands.length + models.length + cities.length}</p>
              <p className="text-sm text-gray-500 mt-1">Páginas indexables</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-medium mb-2">Sitemap</h3>
              <a 
                href={`${API_URL}/api/seo/sitemap`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Ver sitemap dinámico
              </a>
              <p className="text-sm text-gray-500 mt-1">Para Google Search Console</p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <h3 className="font-medium mb-2">Búsqueda SEO</h3>
              <p className="text-sm text-gray-500">API endpoint:</p>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded">/api/seo/search?q=</code>
            </div>
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editModal.data ? 'Editar' : 'Crear'} {editModal.type === 'brand' ? 'Marca' : editModal.type === 'model' ? 'Modelo' : 'Ciudad'}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setEditModal({ open: false, type: null, data: null })}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre</Label>
                  <Input 
                    value={formData.name || ''} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input 
                    value={formData.slug || ''} 
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {editModal.type === 'model' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Marca</Label>
                      <select 
                        value={formData.brand_id || ''}
                        onChange={(e) => {
                          const brand = brands.find(b => b.brand_id === e.target.value);
                          setFormData({ ...formData, brand_id: e.target.value, brand_name: brand?.name || '' });
                        }}
                        className="mt-1 w-full border rounded-md px-3 py-2"
                      >
                        <option value="">Seleccionar marca</option>
                        {brands.map(b => (
                          <option key={b.brand_id} value={b.brand_id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Categoría</Label>
                      <select 
                        value={formData.category || ''}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="mt-1 w-full border rounded-md px-3 py-2"
                      >
                        <option value="">Seleccionar</option>
                        <option value="consolas">Consolas</option>
                        <option value="mandos">Mandos</option>
                        <option value="moviles">Móviles</option>
                        <option value="robots">Robots Aspiradores</option>
                        <option value="patinetes">Patinetes</option>
                        <option value="aspiradores">Aspiradores</option>
                        <option value="electrodomesticos">Electrodomésticos</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tiempo de Reparación</Label>
                      <Input 
                        value={formData.repair_time || ''} 
                        onChange={(e) => setFormData({ ...formData, repair_time: e.target.value })}
                        placeholder="3-5 días"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Rango de Precio</Label>
                      <Input 
                        value={formData.repair_cost_range || ''} 
                        onChange={(e) => setFormData({ ...formData, repair_cost_range: e.target.value })}
                        placeholder="39€ - 99€"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Problemas Comunes (separados por coma)</Label>
                    <Input 
                      value={Array.isArray(formData.common_problems) ? formData.common_problems.join(', ') : ''}
                      onChange={(e) => setFormData({ ...formData, common_problems: e.target.value.split(',').map(s => s.trim()) })}
                      placeholder="Pantalla rota, Batería, No enciende"
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              {editModal.type === 'city' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Provincia</Label>
                    <Input 
                      value={formData.province || ''} 
                      onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Teléfono Local</Label>
                    <Input 
                      value={formData.local_phone || ''} 
                      onChange={(e) => setFormData({ ...formData, local_phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>Descripción</Label>
                <textarea 
                  value={formData.description || formData.intro_text || ''} 
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    description: e.target.value,
                    intro_text: editModal.type === 'city' ? e.target.value : formData.intro_text
                  })}
                  className="mt-1 w-full border rounded-md px-3 py-2 h-24"
                />
              </div>

              <div>
                <Label>Meta Title (SEO)</Label>
                <Input 
                  value={formData.meta_title || ''} 
                  onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Meta Description (SEO)</Label>
                <textarea 
                  value={formData.meta_description || ''} 
                  onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-2 h-20"
                  maxLength={160}
                />
                <p className="text-xs text-gray-500 mt-1">{(formData.meta_description || '').length}/160 caracteres</p>
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditModal({ open: false, type: null, data: null })}>
                Cancelar
              </Button>
              <Button className="bg-[#0047FF] hover:bg-[#003BCC]" onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSEO;
